/**
 * Config-audit chip color pairs (DNKADM-0269) — moved here from
 * `src/features/config-audit/ConfigAuditLog.tsx` (the per-action chip tints,
 * `ACTION_STYLES` + its fallback) so the feature dir stays literal-free (the
 * DNKADM-0269 ESLint guard bans raw colors in `src/features/**`;
 * `src/theme/**` owns them).
 *
 * LIGHT values are byte-identical to the pre-0269 literals (the 0266 identity
 * constraint). DARK values follow the shared translucent-tint + pastel-ink
 * ramp used across the sibling `activity`/`transactionLedger` maps — the same
 * four-family palette (green/purple/red) recurs across the three because the
 * underlying feeds share the same design-handoff chip colors.
 */
import type { ConfigAction } from '@/api/configAudit';

/** One action's chip tint/ink pair + label. */
interface ConfigActionStyle {
  label: string;
  bg: string;
  fg: string;
}

/** Chip tint/ink only (no label) — the unknown-action fallback shape. */
interface ConfigActionTint {
  bg: string;
  fg: string;
}

export interface ConfigAuditMaps {
  /** Keyed by the API's {@link ConfigAction} vocabulary (`src/api/configAudit`). */
  action: Record<ConfigAction, ConfigActionStyle>;
  /** Unknown/unmapped action — same shape `ActionChip` falls back to. */
  actionFallback: ConfigActionTint;
}

const LIGHT: ConfigAuditMaps = {
  action: {
    create: { label: 'Create', bg: '#E5F1E9', fg: '#2E7D52' },
    update: { label: 'Update', bg: '#E9E6F2', fg: '#5B4B9E' },
    delete: { label: 'Delete', bg: '#F6E4E0', fg: '#C0492E' },
  },
  actionFallback: { bg: '#EDEAE4', fg: 'rgba(0,0,0,0.6)' },
};

const DARK: ConfigAuditMaps = {
  action: {
    create: { label: 'Create', bg: 'rgba(46,125,82,0.24)', fg: '#7FD3A3' },
    update: { label: 'Update', bg: 'rgba(91,75,158,0.22)', fg: '#B3ABCB' },
    delete: { label: 'Delete', bg: 'rgba(192,73,46,0.18)', fg: '#F0A090' },
  },
  actionFallback: { bg: 'rgba(184,192,186,0.18)', fg: '#B8C0BA' },
};

/** Per-mode config-audit chip pairs. Light === the pre-0269 literals. */
export function buildConfigAuditMaps(mode: 'light' | 'dark'): ConfigAuditMaps {
  return mode === 'dark' ? DARK : LIGHT;
}
